/**
 * Labiarge autoplay fallback for hero / feature-banner background videos.
 *
 * Problem: Safari (desktop + iOS) and some Android browsers reject the
 * `autoplay` HTML attribute even with `muted` + `playsinline`, common
 * triggers are Low Power Mode, Data Saver, or the tab starting in the
 * background. The HTML attribute fires before the browser has a user-
 * activation signal to override.
 *
 * Fix: for every <video data-labiarge-autoplay> we:
 *   1. Force `muted = true` and `playsInline = true` via the DOM (some
 *      engines honour the property but ignore the attribute).
 *   2. Call play() once on load. If the returned promise rejects, we
 *      retry play() as soon as the video scrolls into view (most fails
 *      are below-the-fold banners whose play-before-visible call races
 *      the Intersection gate in WebKit).
 *   3. Retry once more on the next user interaction (pointerdown /
 *      touchstart / keydown). This covers Low Power Mode, where WebKit
 *      only unlocks after any user gesture on the page.
 *
 * Reduced-motion is handled on the lookbook-cover element; we don't
 * re-check it here so the two modules stay orthogonal.
 */

const SELECTOR = 'video[data-labiarge-autoplay]';

/**
 * @param {HTMLVideoElement} video
 * @returns {Promise<void>}
 */
function tryPlay(video) {
  // Defence-in-depth: set muted/playsInline as DOM props, not just
  // attributes. WebKit is stricter about the property form.
  video.muted = true;
  video.playsInline = true;
  const result = video.play();
  return result instanceof Promise ? result : Promise.resolve();
}

/**
 * @param {HTMLVideoElement} video
 */
function wire(video) {
  if (video.dataset.labiargeAutoplayWired === 'true') return;
  video.dataset.labiargeAutoplayWired = 'true';

  tryPlay(video).catch(() => {
    // First attempt failed. Retry when the video enters the viewport.
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              tryPlay(video).catch(() => {
                /* still blocked. Wait for user gesture (handled below) */
              });
            }
          }
        },
        { threshold: 0.1 }
      );
      io.observe(video);
    }

    // Final fallback: unlock on first user gesture. One-shot listeners.
    const unlock = () => {
      tryPlay(video).catch(() => {
        /* Genuine Low Power Mode rejection. Poster will stay visible. */
      });
    };
    const opts = { once: true, passive: true };
    window.addEventListener('pointerdown', unlock, opts);
    window.addEventListener('touchstart', unlock, opts);
    window.addEventListener('keydown', unlock, opts);
  });
}

function init() {
  for (const video of document.querySelectorAll(SELECTOR)) {
    if (video instanceof HTMLVideoElement) wire(video);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// Theme Editor re-renders sections without a full reload; re-scan so newly
// inserted videos get wired too.
document.addEventListener('shopify:section:load', init);
